import { useEffect, useState } from 'react';

import { Aggregated, PostcodeMarker } from '../CountryView/types';
import { Incident, pointInFeature } from '../MapSharedData';
import { PostcodeIndex, lookupPostcode } from '../parseGeonames';

interface UseUnifiedAggregationOptions {
  activeCountry: string | null;
  incidents: Incident[];
  postcodes: PostcodeIndex | null;
  regionsGeoJson: GeoJSON.FeatureCollection | null;
  regionNameProperty: string;
}

interface AggregationResult {
  aggregated: Aggregated;
  allMarkers: PostcodeMarker[];
  unmatchedCount: number;
  /**
   * Country the aggregation was computed for. Lets downstream hooks reject
   * stale data when activeCountry has already moved on but the upstream
   * geojson fetch hasn't finished. Null on the world view.
   */
  country: string | null;
}

const EMPTY: AggregationResult = {
  aggregated: {},
  allMarkers: [],
  unmatchedCount: 0,
  country: null,
};

/**
 * Builds the per-region aggregation for the active country. Mirrors the
 * legacy CountryView useAggregation but tracks the country alongside the
 * result so the persistent UnifiedMap can verify freshness — when the user
 * jumps between countries faster than geojson loads, downstream hooks need a
 * way to ignore the briefly-stale state.
 */
export function useUnifiedAggregation({
  activeCountry,
  incidents,
  postcodes,
  regionsGeoJson,
  regionNameProperty,
}: UseUnifiedAggregationOptions): AggregationResult {
  const [result, setResult] = useState<AggregationResult>(EMPTY);

  useEffect(() => {
    if (!activeCountry || !postcodes || !regionsGeoJson) {
      setResult(EMPTY);
      return;
    }

    const regions: Aggregated = {};
    const markers: PostcodeMarker[] = [];
    let unmatched = 0;

    for (const incident of incidents) {
      const rawCode = incident.postcode;
      const hasCode = typeof rawCode === 'string' && rawCode.trim() !== '';
      const entry = hasCode ? lookupPostcode(postcodes, rawCode) : null;

      if (!entry) {
        const regionName = 'Unassigned';
        const marker: PostcodeMarker = {
          postcode: hasCode ? (rawCode as string) : '—',
          place: hasCode ? 'Unknown location' : 'No postcode',
          count: incident.count,
          region: regionName,
          lng: NaN,
          lat: NaN,
        };
        if (!regions[regionName]) regions[regionName] = { total: 0, postcodes: [] };
        regions[regionName].total += incident.count;
        regions[regionName].postcodes.push(marker);
        unmatched++;
        continue;
      }

      const feature = regionsGeoJson.features.find((f) =>
        pointInFeature([entry.lng, entry.lat], f),
      );
      const regionName = feature?.properties?.[regionNameProperty] ?? 'Unassigned';

      const marker: PostcodeMarker = {
        postcode: incident.postcode,
        place: entry.place,
        count: incident.count,
        region: regionName,
        lng: entry.lng,
        lat: entry.lat,
      };
      markers.push(marker);

      if (!regions[regionName]) regions[regionName] = { total: 0, postcodes: [] };
      regions[regionName].total += incident.count;
      regions[regionName].postcodes.push(marker);
    }

    setResult({
      aggregated: regions,
      allMarkers: markers,
      unmatchedCount: unmatched,
      country: activeCountry,
    });
  }, [activeCountry, incidents, postcodes, regionsGeoJson, regionNameProperty]);

  return result;
}
